import { Box, Divider, MenuItem, Stack, Typography } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { enqueueSnackbar } from "notistack";
import DisplayErrors from "src/components/display-errors";
import Iconify from "src/components/iconify/iconify";
import TruxTextField from "src/components/inputs/trux-text-field";
import SimpleDialog from "src/components/simple-dialog";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { RentalCustomer } from "src/models";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { Constants } from "src/constants";

type RentalCustomerModalProps = {
  selectedRentalCustomerModel: ModelSavingData<RentalCustomer>,
  isOpen: boolean,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  updateRentalCustomer: VoidFunction,
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Stack>
      <Typography variant="subtitle2">{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Stack>
  );
}

export function RentalCustomerModal(
  { selectedRentalCustomerModel, isOpen, setIsOpen, updateRentalCustomer }: RentalCustomerModalProps
) {

  // mirrors the backend ValidateRentalCustomer so the form can't be submitted incomplete
  const submit = () => {
    const customer = selectedRentalCustomerModel.model;
    if (!customer) {
      return;
    }
    if (!customer.companyName.trim() && !customer.firstName.trim() && !customer.lastName.trim()) {
      enqueueSnackbar("Please enter either a Company Name or a First Name or a Last Name", { variant: "error" });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim())) {
      enqueueSnackbar("Please enter a valid Email", { variant: "error" });
      return;
    }
    if (!customer.username.trim()) {
      enqueueSnackbar("Please enter the Username", { variant: "error" });
      return;
    }
    if (selectedRentalCustomerModel.isNew && !customer.password.trim()) {
      enqueueSnackbar("Please enter the Password", { variant: "error" });
      return;
    }
    updateRentalCustomer();
  };


  return selectedRentalCustomerModel.model && <SimpleDialog
    title={<>{selectedRentalCustomerModel.isNew ? "New Customer" : "Update Customer"}</>}
    open={isOpen}
    size="medium"
    onCancel={() => {
      setIsOpen(false);
    }}
    onSubmit={submit}
    submitDisabled={selectedRentalCustomerModel.loading}
    submitButton={selectedRentalCustomerModel.isNew ? "New Customer" : "Update Customer"}
    submitButtonIcon={selectedRentalCustomerModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
    submitButtonColor={selectedRentalCustomerModel.isNew ? "success" : "info"}>


    <DisplayErrors savingData={selectedRentalCustomerModel} />

    <Stack direction="column" gap={2}>

      <SectionHeader
        title="Customer"
        subtitle="At least one of Company, First Name or Last Name is required"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
          <TruxTextField modelSavingData={selectedRentalCustomerModel} field="companyName" label="Company" />
        </Box>
        <TruxTextField modelSavingData={selectedRentalCustomerModel} field="firstName" label="First Name" />
        <TruxTextField modelSavingData={selectedRentalCustomerModel} field="lastName" label="Last Name" />
        <TruxTextField modelSavingData={selectedRentalCustomerModel} field="phone" label="Phone" />
        <TruxTextField modelSavingData={selectedRentalCustomerModel} field="email" label="Email *" />
        <Box sx={{ gridColumn: { sm: "1 / -1" }, mt: -1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Insurance requests and rental agreements are sent to this address
          </Typography>
        </Box>
      </Box>

      <Divider />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
          gap: 2,
        }}
      >
        <TruxTextField modelSavingData={selectedRentalCustomerModel} field={"dotNumber"} label="DOT Number"/>
        <TruxTextField modelSavingData={selectedRentalCustomerModel} field={"mcNumber"} label="MC Number"/>
        <TruxTextField modelSavingData={selectedRentalCustomerModel} field={"feinNumber"} label="FEIN Number"/>
      </Box>
      <Divider />
      <SectionHeader title="Address" />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <TruxSimpleTextField label="Street 1" value={selectedRentalCustomerModel.model.address.street1} onChange={(val) => selectedRentalCustomerModel.update((rentalCustomer) => {
          rentalCustomer.address.street1 = val;
        })} />
        <TruxSimpleTextField label="Street 2" value={selectedRentalCustomerModel.model.address.street2} onChange={(val) => selectedRentalCustomerModel.update((rentalCustomer) => {
          rentalCustomer.address.street2 = val;
        })} />
        <TruxSimpleTextField label="City" value={selectedRentalCustomerModel.model.address.city} onChange={(val) => selectedRentalCustomerModel.update((rentalCustomer) => {
          rentalCustomer.address.city = val;
        })} />
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box flex={1}>
            <TruxSimpleSelectField value={selectedRentalCustomerModel.model.address.state} onChange={(val) => selectedRentalCustomerModel.update((rentalCustomer) => {
              rentalCustomer.address.state = val;
            })}>
              <MenuItem value="">State</MenuItem>
              {Constants.STATES.map((state) => <MenuItem value={state} key={state}>{state}</MenuItem>)}
            </TruxSimpleSelectField>
          </Box>
          <Box flex={1}>
            <TruxSimpleTextField label="Zip" value={selectedRentalCustomerModel.model.address.zip} onChange={(val) => selectedRentalCustomerModel.update((rentalCustomer) => {
              rentalCustomer.address.zip = val;
            })} />
          </Box>
        </Box>
      </Box>

      <Divider />
      <SectionHeader
        title="Portal Login"
        subtitle="The Customer logs into the portal with this Username and Password"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <TruxTextField modelSavingData={selectedRentalCustomerModel} field="username" label="Username *" />
        <TruxTextField
          modelSavingData={selectedRentalCustomerModel}
          field="password"
          label={selectedRentalCustomerModel.isNew ? "Password *" : "Password"}
        />
        {!selectedRentalCustomerModel.isNew && (
          <Box sx={{ gridColumn: { sm: "1 / -1" }, mt: -1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Leave the password empty to keep the current one
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />

      <TruxTextField modelSavingData={selectedRentalCustomerModel} field="comment" label="Comment" multiline minRows={2} />

      <Stack direction="row" alignItems="center" gap={1}>
        <TruxCheckboxField modelSavingData={selectedRentalCustomerModel} field="active" />
        <Typography variant="body2">Active</Typography>
      </Stack>
    </Stack>
  </SimpleDialog >
}
